import { BoxProps } from '@leagueplatform/genesis-commons';
import {
  APP_MANAGED_ROUTES,
  EXTERNAL_SSO_LINK,
  EXTERNAL_LINK,
} from '../common/constants';

export const boldTextLink: BoxProps = {
  color: 'onSurface.text.subdued',
  fontWeight: 'bold',
};

export const boldTextCriticalLink: BoxProps = {
  color: 'onSurface.text.critical',
  fontWeight: 'bold',
};

type LocalesKey =
  | 'STR_PERSONAL_INFO'
  | 'STR_MANAGE_PHARMACY_SETTINGS'
  | 'STR_PDP_PREMIUM_PAYMENTS'
  | 'STR_SUPPLEMENTAL_PREMIUM_PAYMENT'
  | 'STR_MEDICARE_PREMIUM_PAYMENT'
  | 'STR_SECURITY_SETTINGS'
  | 'STR_CUSTOMER_SUPPORT'
  | 'STR_FORMS_CENTER'
  | 'STR_PRIVACY_POLICY'
  | 'STR_PRIVACY_NOTICES'
  | 'STR_PRIVACY_FORMS'
  | 'STR_HIPPA_NOTICE_OF_PRIVACY_PRACTICES'
  | 'STR_GRAMM_LEACH_BILLEY_ACT_NOTICE'
  | 'STR_NONDISCRIMINATION_NOTICE'
  | 'STR_WASHINGTON_CONSUMER_PRIVACY_NOTICE'
  | 'STR_APPEALS'
  | 'STR_GRIEVANCES'
  | 'STR_DISCLAIMERS'
  | 'STR_ABOUT_CIGNA_HEALTHCARE'
  | 'STR_LANGUAGE_ASSISTANCE'
  | 'STR_TERMS_OF_USE'
  | 'STR_DELETE_YOUR_ACCOUNT'
  | 'STR_LOG_OUT';

interface Link {
  text: string;
  to: string;
  target?: '_blank';
  onClick?: () => void;
  color: '$onSurfaceTextSubdued' | '$onSurfaceTextCritical';
  linkIcon: 'interfaceChevronRight' | 'interfaceExternalLink';
  linkProps?: typeof boldTextLink | typeof boldTextCriticalLink;
}

type LinkType =
  | 'personalInfo'
  | 'managePharmacySettings'
  | 'pdpPremium'
  | 'supplementalPremium'
  | 'medicarePremium'
  | 'securitySettings'
  | 'customerSupport'
  | 'formsCenter'
  | 'privacyPolicy'
  | 'privacyNotices'
  | 'privacyForms'
  | 'hippaNotice'
  | 'grammLeach'
  | 'nondiscrimination'
  | 'washingtonPrivacy'
  | 'appeals'
  | 'grievances'
  | 'disclaimers'
  | 'aboutCigna'
  | 'languageAssistance'
  | 'termsOfUse'
  | 'deleteAccount'
  | 'logOut';

export function createLink(
  type: LinkType,
  formatMessage: (descriptor: any) => string,
  onClick?: () => void,
): Link {
  const defaults = {
    color: '$onSurfaceTextSubdued' as const,
    linkProps: boldTextLink,
  };

  const overrides =
    type === 'logOut'
      ? {
          color: '$onSurfaceTextCritical' as const,
          linkProps: boldTextCriticalLink,
        }
      : {};

  const linkConfigs: Record<
    LinkType,
    Omit<Link, 'text' | 'color' | 'linkProps'>
  > = {
    personalInfo: {
      to: APP_MANAGED_ROUTES.PROFILE_SETTINGS,
      linkIcon: 'interfaceChevronRight',
    },
    managePharmacySettings: {
      to: EXTERNAL_SSO_LINK.MANAGE_PHARMACY_SETTINGS,
      linkIcon: 'interfaceExternalLink',
    },
    pdpPremium: {
      to: APP_MANAGED_ROUTES.PDP_PREMIUM_PAYMENT,
      linkIcon: 'interfaceChevronRight',
    },
    supplementalPremium: {
      to: APP_MANAGED_ROUTES.SUPPLEMENTAL_PREMIUM_PAYMENT,
      linkIcon: 'interfaceChevronRight',
    },
    medicarePremium: {
      to: APP_MANAGED_ROUTES.MEDICARE_PREMIUM_PAYMENT,
      linkIcon: 'interfaceExternalLink',
    },
    securitySettings: {
      to: APP_MANAGED_ROUTES.SECURITY_SETTINGS,
      linkIcon: 'interfaceChevronRight',
    },
    customerSupport: {
      to: APP_MANAGED_ROUTES.CUSTOMER_SUPPORT,
      linkIcon: 'interfaceChevronRight',
    },
    formsCenter: {
      to: APP_MANAGED_ROUTES.FORMS_CENTER_ROUTE,
      linkIcon: 'interfaceChevronRight',
    },
    privacyPolicy: {
      to: EXTERNAL_LINK.PRIVACY_POLICY,
      target: '_blank',
      linkIcon: 'interfaceExternalLink',
    },
    privacyNotices: {
      to: EXTERNAL_LINK.PRIVACY_NOTICES,
      target: '_blank',
      linkIcon: 'interfaceExternalLink',
    },
    privacyForms: {
      to: EXTERNAL_LINK.PRIVACY_FORMS,
      target: '_blank',
      linkIcon: 'interfaceExternalLink',
    },
    hippaNotice: {
      to: EXTERNAL_LINK.HIPPA_NOTICE_OF_PRIVACY_PRACTICES,
      target: '_blank',
      linkIcon: 'interfaceExternalLink',
    },
    grammLeach: {
      to: EXTERNAL_LINK.GRAMM_LEACH_BILLEY_ACT_NOTICE,
      target: '_blank',
      linkIcon: 'interfaceExternalLink',
    },
    nondiscrimination: {
      to: EXTERNAL_LINK.NONDISCRIMINATION_NOTICE,
      target: '_blank',
      linkIcon: 'interfaceExternalLink',
    },
    washingtonPrivacy: {
      to: EXTERNAL_LINK.WASHINGTON_CONSUMER_PRIVACY_NOTICE,
      target: '_blank',
      linkIcon: 'interfaceExternalLink',
    },
    appeals: {
      to: EXTERNAL_LINK.APPEALS,
      target: '_blank',
      linkIcon: 'interfaceExternalLink',
    },
    grievances: {
      to: EXTERNAL_LINK.GRIEVANCES,
      target: '_blank',
      linkIcon: 'interfaceExternalLink',
    },
    disclaimers: {
      to: EXTERNAL_LINK.DISCLAIMERS,
      target: '_blank',
      linkIcon: 'interfaceExternalLink',
    },
    aboutCigna: {
      to: APP_MANAGED_ROUTES.ABOUT_CIGNA_ROUTE,
      linkIcon: 'interfaceChevronRight',
    },
    languageAssistance: {
      to: EXTERNAL_LINK.LANGUAGE_ASSISTANCE,
      target: '_blank',
      linkIcon: 'interfaceExternalLink',
    },
    termsOfUse: {
      to: EXTERNAL_LINK.TERMS_OF_USE,
      target: '_blank',
      linkIcon: 'interfaceExternalLink',
    },
    deleteAccount: {
      to: '#',
      onClick,
      linkIcon: 'interfaceChevronRight',
    },
    logOut: {
      to: APP_MANAGED_ROUTES.SIGN_OUT,
      linkIcon: 'interfaceChevronRight',
    },
  };

  const messageIds: Record<LinkType, LocalesKey> = {
    personalInfo: 'STR_PERSONAL_INFO',
    managePharmacySettings: 'STR_MANAGE_PHARMACY_SETTINGS',
    pdpPremium: 'STR_PDP_PREMIUM_PAYMENTS',
    supplementalPremium: 'STR_SUPPLEMENTAL_PREMIUM_PAYMENT',
    medicarePremium: 'STR_MEDICARE_PREMIUM_PAYMENT',
    securitySettings: 'STR_SECURITY_SETTINGS',
    customerSupport: 'STR_CUSTOMER_SUPPORT',
    formsCenter: 'STR_FORMS_CENTER',
    privacyPolicy: 'STR_PRIVACY_POLICY',
    privacyNotices: 'STR_PRIVACY_NOTICES',
    privacyForms: 'STR_PRIVACY_FORMS',
    hippaNotice: 'STR_HIPPA_NOTICE_OF_PRIVACY_PRACTICES',
    grammLeach: 'STR_GRAMM_LEACH_BILLEY_ACT_NOTICE',
    nondiscrimination: 'STR_NONDISCRIMINATION_NOTICE',
    washingtonPrivacy: 'STR_WASHINGTON_CONSUMER_PRIVACY_NOTICE',
    appeals: 'STR_APPEALS',
    grievances: 'STR_GRIEVANCES',
    disclaimers: 'STR_DISCLAIMERS',
    aboutCigna: 'STR_ABOUT_CIGNA_HEALTHCARE',
    languageAssistance: 'STR_LANGUAGE_ASSISTANCE',
    termsOfUse: 'STR_TERMS_OF_USE',
    deleteAccount: 'STR_DELETE_YOUR_ACCOUNT',
    logOut: 'STR_LOG_OUT',
  };

  return {
    ...defaults,
    ...overrides,
    ...linkConfigs[type],
    text: formatMessage({ id: messageIds[type] }),
  };
}
